import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { type NotificationOutput } from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { BrowserPushToggle } from '../components/BrowserPushToggle.js';
import { fmtDateTime } from '../lib/date.js';
import { useUserPreference } from '../lib/user-preferences.js';

export function NotificationsPage() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useUserPreference<'all' | 'unread'>('notifications.filter', 'all', {
    validate: (value) => value === 'all' || value === 'unread',
  });

  const list = useQuery({
    queryKey: ['notifications', { filter, limit: 100 }],
    enabled: api !== null,
    queryFn: async () => {
      const p = new URLSearchParams();
      if (filter === 'unread') p.set('unreadOnly', 'true');
      p.set('limit', '100');
      const res = await fetch(`/v1/notifications?${p.toString()}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: NotificationOutput[]; total: number } };
      return body.data;
    },
  });

  const markRead = useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await fetch('/v1/notifications/mark-read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const res = await fetch('/v1/notifications/mark-all-read', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const unreadCount = (list.data?.items ?? []).filter((n) => n.readAt === null).length;

  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Notifications</h1>
          <p className="text-sm text-muted">{unreadCount} unread</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'all' | 'unread')}
            className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent w-full sm:w-auto"
          >
            <option value="all">All</option>
            <option value="unread">Unread only</option>
          </select>
          <button
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending || unreadCount === 0}
            className="bg-bg-panel hover:bg-bg-hover border border-border text-white text-sm font-medium px-3 py-2 rounded-lg disabled:opacity-50 w-full sm:w-auto"
          >
            Mark all read
          </button>
        </div>
      </header>

      <BrowserPushToggle />

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden">
        {list.isLoading && <p className="px-4 py-8 text-center text-muted">Loading…</p>}
        {list.error && <p className="px-4 py-8 text-center text-rose-400">{(list.error as Error).message}</p>}
        {list.data?.items.length === 0 && <p className="px-4 py-8 text-center text-muted">No notifications.</p>}
        {list.data?.items.map((n) => (
          <div
            key={n.id}
            className={`flex items-start gap-3 px-4 py-3 border-b border-border/50 ${n.readAt ? 'opacity-60' : 'bg-bg-hover/20'}`}
          >
            <div className="flex-1 min-w-0">
              <Link
                to={n.actionUrl ?? '#'}
                className="text-sm font-medium text-white hover:text-accent block truncate"
              >
                {n.title}
              </Link>
              <p className="text-sm text-muted mt-0.5 break-words">{n.body}</p>
              <p className="text-xs text-muted mt-1">{fmtDateTime(n.createdAt)}</p>
            </div>
            {!n.readAt && (
              <button
                onClick={() => markRead.mutate([n.id])}
                disabled={markRead.isPending}
                className="text-xs sm:text-sm text-accent hover:text-emerald-400 whitespace-nowrap min-h-[44px] px-2"
              >
                Mark read
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
